import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import SearchStatusBar from "@/components/common/SearchStatusBar";
import { type Ad, statusBadge } from "@/features/ads/types";
import { Checkbox } from "@/components/ui/checkbox";
import BatchActionBar from "@/features/batch/BatchActionBar";
import { useSelection } from "@/features/batch/useSelection";

const STATUS_OPTIONS = [
  { value: "ALL",      label: "全部状态" },
  { value: "ACTIVE",   label: "ACTIVE" },
  { value: "PAUSED",   label: "PAUSED" },
  { value: "DELETED",  label: "DELETED" },
  { value: "ARCHIVED", label: "ARCHIVED" },
];

export default function AdSetDetailPage() {
  const { accountId = "", adsetId = "" } = useParams();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const sel = useSelection();
  const invalidateAds = () => qc.invalidateQueries({ queryKey: ["ads", accountId, adsetId] });

  const ads = useQuery({
    queryKey: ["ads", accountId, adsetId],
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/v1/ad-accounts/{accountId}/adsets/{adsetId}/ads",
        { params: { path: { accountId, adsetId } } }
      );
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Ad[];
    },
    staleTime: 15_000,
  });

  const filteredAds = useMemo(() => {
    const rows = ads.data ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((a) => {
      if (status !== "ALL" && a.status !== status) return false;
      if (needle && !((a.name ?? "") + " " + a.id).toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [ads.data, q, status]);

  const copy = useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await api.POST(
        "/api/v1/ad-accounts/{accountId}/ads/{adId}/copy",
        { params: { path: { accountId, adId: id } }, body: { newStatus: "PAUSED" } }
      );
      if (error || !response.ok) throw error || new Error("复制失败");
      return data as unknown as { newId: string };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["ads", accountId, adsetId] });
      toast.success(`复制成功：${r.newId}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE(
        "/api/v1/ad-accounts/{accountId}/ads/{adId}",
        { params: { path: { accountId, adId: id } } }
      );
      if (error || !response.ok) throw error || new Error("删除失败");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ads", accountId, adsetId] });
      toast.success("已删除");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/ad-accounts/${accountId}`} className="text-xs text-muted-foreground hover:underline">
          ← 返回广告账户
        </Link>
        <h1 className="text-2xl font-semibold mt-2">AdSet</h1>
        <div className="font-mono text-xs text-muted-foreground mt-1">{adsetId}</div>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-medium text-lg">Ads</h2>
          <SearchStatusBar
            q={q}
            onQChange={setQ}
            status={status}
            onStatusChange={setStatus}
            statusOptions={STATUS_OPTIONS}
            placeholder="搜索 Ad 名称或 ID"
          />
        </div>

        {sel.count > 0 && (
          <BatchActionBar
            accountId={accountId}
            level="ads"
            resourceLabel="Ad"
            supportsBudget={false}
            selectedIds={sel.ids}
            onClear={sel.clear}
            onSuccess={invalidateAds}
          />
        )}

        <div className="rounded-lg border border-border bg-background overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium w-8">
                  <Checkbox
                    aria-label="全选"
                    checked={
                      filteredAds.length > 0 && filteredAds.every((a) => sel.has(a.id))
                        ? true
                        : filteredAds.some((a) => sel.has(a.id))
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(v) => {
                      if (v === true) sel.setAll(filteredAds.map((a) => a.id));
                      else sel.clear();
                    }}
                  />
                </th>
                <th className="text-left px-4 py-2 font-medium">名称</th>
                <th className="text-left px-4 py-2 font-medium">Creative ID</th>
                <th className="text-left px-4 py-2 font-medium">状态</th>
                <th className="text-right px-4 py-2 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {ads.isLoading && (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">加载中…</td></tr>
              )}
              {!ads.isLoading && ads.data?.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">该 AdSet 暂无 Ad</td></tr>
              )}
              {!ads.isLoading && (ads.data?.length ?? 0) > 0 && filteredAds.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">没有符合筛选条件的 Ad</td></tr>
              )}
              {filteredAds.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="px-4 py-2">
                    <Checkbox
                      aria-label={`选择 ${a.name ?? a.id}`}
                      checked={sel.has(a.id)}
                      onCheckedChange={() => sel.toggle(a.id)}
                    />
                  </td>
                  <td className="px-4 py-2">{a.name ?? "(无名称)"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{a.creativeId ?? "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${statusBadge(a.status)}`}>
                      {a.status ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right space-x-3 text-xs">
                    <button
                      onClick={() => copy.mutate(a.id)}
                      disabled={copy.isPending}
                      className="text-primary hover:underline disabled:opacity-50"
                    >
                      复制
                    </button>
                    <button
                      onClick={() => { if (confirm(`确定删除 Ad "${a.name}"?`)) remove.mutate(a.id); }}
                      disabled={remove.isPending}
                      className="text-destructive hover:underline disabled:opacity-50"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
